import { useEffect, useState } from "react";
import { Mail, Send, Save } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const PRESETS = [
  { label: "Özel SMTP", host: "", port: 587, tls: true },
  { label: "Gmail (uygulama şifresi)", host: "smtp.gmail.com", port: 587, tls: true },
  { label: "Outlook / Office 365", host: "smtp.office365.com", port: 587, tls: true },
  { label: "Yandex", host: "smtp.yandex.com", port: 465, tls: false },
  { label: "Yahoo", host: "smtp.mail.yahoo.com", port: 587, tls: true },
  { label: "SendGrid", host: "smtp.sendgrid.net", port: 587, tls: true },
];

const SMTPTab = () => {
  const [cfg, setCfg] = useState({
    host: "", port: 587, user: "", password: "",
    sender: "", use_tls: true, notify_to: "",
  });
  const [passwordSet, setPasswordSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/smtp");
      setCfg({
        host: data.host || "",
        port: data.port || 587,
        user: data.user || "",
        password: "",
        sender: data.sender || "",
        use_tls: data.use_tls !== false,
        notify_to: data.notify_to || "",
      });
      setPasswordSet(!!data.password_set);
      setTestEmail(data.notify_to || "");
    } catch (e) {
      toast.error("Yükleme başarısız");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const update = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const applyPreset = (p) => {
    if (!p) return;
    update("host", p.host);
    update("port", p.port);
    update("use_tls", p.tls);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (!payload.password) delete payload.password;
      await api.put("/admin/smtp", payload);
      toast.success("Kaydedildi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    if (!testEmail) {
      toast.error("Test için bir e-posta adresi girin");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/smtp/test", { to: testEmail });
      toast.success(data.message || "Test gönderildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test başarısız");
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="py-10 text-[#5C4D3C]">Yükleniyor...</div>;

  return (
    <div data-testid="smtp-tab" className="max-w-3xl space-y-6">
      <div className="bg-[#FDFBF7] border border-[#E2D9C8] p-6 rounded-sm space-y-5">
        <div className="flex items-center gap-3">
          <Mail className="text-[#B5441E]" size={22} />
          <h3 className="font-display text-2xl text-[#2D2824]">SMTP E-posta Ayarları</h3>
        </div>
        <p className="text-sm text-[#5C4D3C] leading-relaxed">
          Bu ayarlar yapıldığında, yeni rezervasyon talebi gelince otomatik olarak
          aşağıda belirttiğiniz e-postaya bildirim gönderilir.
        </p>

        <div className="space-y-2">
          <Label className="text-xs uppercase text-[#5C4D3C]">Hızlı Şablon</Label>
          <Select onValueChange={(v) => applyPreset(PRESETS[parseInt(v, 10)])}>
            <SelectTrigger data-testid="smtp-preset" className="bg-white border-[#E2D9C8]">
              <SelectValue placeholder="Şablon seçin (opsiyonel)" />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.map((p, i) => (
                <SelectItem key={p.label} value={String(i)}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2 space-y-2">
            <Label className="text-xs uppercase text-[#5C4D3C]">SMTP Sunucu</Label>
            <Input
              data-testid="smtp-host"
              value={cfg.host}
              onChange={(e) => update("host", e.target.value)}
              placeholder="örn. smtp.gmail.com"
              className="bg-white border-[#E2D9C8]"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase text-[#5C4D3C]">Port</Label>
            <Input
              type="number"
              data-testid="smtp-port"
              value={cfg.port}
              onChange={(e) => update("port", parseInt(e.target.value || "587", 10))}
              className="bg-white border-[#E2D9C8] ltr-numbers"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase text-[#5C4D3C]">Kullanıcı Adı (genelde e-posta adresiniz)</Label>
          <Input
            data-testid="smtp-user"
            value={cfg.user}
            onChange={(e) => update("user", e.target.value)}
            placeholder="örn. info@fatmahnm.com"
            className="bg-white border-[#E2D9C8]"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase text-[#5C4D3C]">
            Şifre {passwordSet && <span className="text-[#4A5D23] normal-case tracking-normal">— Kayıtlı (boş bırakırsanız değişmez)</span>}
          </Label>
          <Input
            type="password"
            data-testid="smtp-password"
            value={cfg.password}
            onChange={(e) => update("password", e.target.value)}
            placeholder={passwordSet ? "•••••••• (mevcut şifre saklı)" : "Yeni şifre"}
            className="bg-white border-[#E2D9C8]"
            autoComplete="new-password"
          />
          <p className="text-[11px] text-[#5C4D3C]">
            Gmail için <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-[#B5441E] underline">Uygulama Şifresi</a> kullanın (normal hesap şifresi değil).
          </p>
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase text-[#5C4D3C]">Gönderen Adresi (From) — opsiyonel</Label>
          <Input
            data-testid="smtp-sender"
            value={cfg.sender}
            onChange={(e) => update("sender", e.target.value)}
            placeholder='örn. "Fatma Hanım Konağı" <info@fatmahnm.com>'
            className="bg-white border-[#E2D9C8]"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase text-[#5C4D3C]">Bildirim Alacak E-posta</Label>
          <Input
            data-testid="smtp-notify"
            value={cfg.notify_to}
            onChange={(e) => update("notify_to", e.target.value)}
            placeholder="Rezervasyon bildirimleri buraya gönderilecek"
            className="bg-white border-[#E2D9C8]"
          />
        </div>

        <div className="flex items-center gap-3">
          <input
            id="smtp-tls"
            type="checkbox"
            data-testid="smtp-tls"
            checked={cfg.use_tls}
            onChange={(e) => update("use_tls", e.target.checked)}
            className="w-4 h-4 accent-[#B5441E]"
          />
          <Label htmlFor="smtp-tls" className="text-sm text-[#5C4D3C] cursor-pointer">
            STARTTLS kullan (587 portu için tavsiye edilir, 465 portu için kapatın)
          </Label>
        </div>

        <Button
          onClick={save}
          disabled={saving}
          data-testid="smtp-save"
          className="bg-[#B5441E] hover:bg-[#9D3A19] text-white uppercase tracking-[0.2em] text-xs py-6 px-8"
        >
          <Save size={14} className="me-2" />
          {saving ? "Kaydediliyor..." : "Ayarları Kaydet"}
        </Button>
      </div>

      <div className="bg-[#FDFBF7] border border-[#E2D9C8] p-6 rounded-sm space-y-4">
        <h3 className="font-display text-xl text-[#2D2824]">Test E-postası Gönder</h3>
        <p className="text-sm text-[#5C4D3C]">
          Ayarları kaydettikten sonra buradan test e-postası gönderip yapılandırmanın
          doğru olduğunu hemen kontrol edebilirsiniz.
        </p>
        <div className="flex gap-3">
          <Input
            data-testid="smtp-test-to"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="test@example.com"
            className="bg-white border-[#E2D9C8] flex-1"
          />
          <Button
            onClick={test}
            disabled={testing}
            data-testid="smtp-test-send"
            className="bg-[#4A5D23] hover:bg-[#3A4A1C] text-white uppercase tracking-[0.2em] text-xs px-6"
          >
            <Send size={14} className="me-2" />
            {testing ? "Gönderiliyor..." : "Test Gönder"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SMTPTab;
